import React from 'react';
import { motion } from 'framer-motion';

interface ProgressRingProps {
    progress: number; // 0-100
    size?: number;
    strokeWidth?: number;
    color?: string;
    backgroundColor?: string;
    showLabel?: boolean;
    labelText?: string;
    animated?: boolean;
}

/**
 * Circular progress indicator with optional animation.
 * Used for course completion tracking and module progress.
 */
export const ProgressRing: React.FC<ProgressRingProps> = ({
    progress,
    size = 60,
    strokeWidth = 4,
    color = '#22c55e',
    backgroundColor = '#e2e8f0',
    showLabel = true,
    labelText,
    animated = true
}) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
        <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="transform -rotate-90">
                {/* Background circle */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={backgroundColor}
                    strokeWidth={strokeWidth}
                    fill="none"
                />
                {/* Progress circle */}
                <motion.circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={color}
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeLinecap="round"
                    initial={animated ? { strokeDashoffset: circumference } : { strokeDashoffset }}
                    animate={{ strokeDashoffset }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                    style={{
                        strokeDasharray: circumference,
                    }}
                />
            </svg>

            {/* Center label */}
            {showLabel && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-slate-600">
                        {labelText || `${Math.round(progress)}%`}
                    </span>
                </div>
            )}
        </div>
    );
};

export default ProgressRing;
